/**
 * Users Service
 * Handles user profile management and admin user CRUD operations
 */

import apiClient from "../client";
import { API_ENDPOINTS } from "../../constants/api.constants";

const usersService = {
  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  async getMe() {
    const { data } = await apiClient.get(API_ENDPOINTS.USERS.ME);
    return data;
  },

  /**
   * Update current user profile
   * @param {Object} userData - User data to update
   * @param {string} [userData.first_name] - First name
   * @param {string} [userData.last_name] - Last name
   * @param {string} [userData.email] - Email address
   * @param {string} [userData.phone] - Phone number
   * @returns {Promise<Object>} Updated user profile
   */
  async update(userData) {
    const { data } = await apiClient.put(API_ENDPOINTS.USERS.UPDATE, userData);
    return data;
  },

  /**
   * Upload or replace the current user's profile photo.
   * @param {File} file - Image file (jpeg/png)
   * @returns {Promise<Object>} Updated user profile
   */
  async uploadAvatar(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post(API_ENDPOINTS.USERS.MY_AVATAR, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  /**
   * Remove the current user's profile photo.
   * @returns {Promise<Object>} Updated user profile
   */
  async deleteAvatar() {
    const { data } = await apiClient.delete(API_ENDPOINTS.USERS.MY_AVATAR);
    return data;
  },

  /**
   * Fetch a user's profile photo as an object URL.
   *
   * The endpoint requires a Bearer token, which a plain <img src> cannot
   * send, so the bytes are fetched through the authenticated client and
   * wrapped in a blob URL. Callers must revokeObjectURL when done.
   * @param {string} userId
   * @returns {Promise<string|null>} Object URL, or null when no photo is set
   */
  async fetchAvatarUrl(userId) {
    try {
      const { data } = await apiClient.get(API_ENDPOINTS.USERS.AVATAR(userId), {
        responseType: "blob",
      });
      return URL.createObjectURL(data);
    } catch {
      // 404 simply means "no photo set" — the caller falls back to initials.
      return null;
    }
  },

  /**
   * Change current user password
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.current_password - Current password
   * @param {string} passwordData.new_password - New password
   * @param {string} passwordData.confirm_password - Confirm new password
   * @returns {Promise<Object>} Success message
   */
  async changePassword(passwordData) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      passwordData,
    );
    return data;
  },

  // ============== Admin User Management ==============

  /**
   * Get all users with optional filters (Admin only)
   * @param {Object} filters - Filter parameters
   * @param {number} [filters.skip=0] - Number of records to skip
   * @param {number} [filters.limit=20] - Number of records to return
   * @param {string} [filters.search] - Search by name or email
   * @param {string} [filters.role] - Filter by role (owner, admin, coach, parent)
   * @param {boolean} [filters.is_active] - Filter by active status
   * @returns {Promise<Object>} Response with items, total, skip, limit
   */
  async getAll(filters = {}) {
    const { data } = await apiClient.get(API_ENDPOINTS.USERS.LIST, {
      params: filters,
    });
    return data;
  },

  /**
   * Get user by ID (Admin only)
   * @param {string} id - User ID
   * @returns {Promise<Object>} User details
   */
  async getById(id) {
    const { data } = await apiClient.get(API_ENDPOINTS.USERS.BY_ID(id));
    return data;
  },

  /**
   * Create a new user (Admin only)
   * @param {Object} userData - User data
   * @param {string} userData.email - Email address
   * @param {string} userData.first_name - First name
   * @param {string} userData.last_name - Last name
   * @param {string} [userData.phone] - Phone number
   * @param {string} [userData.role='parent'] - User role
   * @param {string} [userData.password] - Password (optional, user will need to reset if not provided)
   * @param {boolean} [userData.is_active=true] - Active status
   * @returns {Promise<Object>} Created user
   */
  async create(userData) {
    const { data } = await apiClient.post(API_ENDPOINTS.USERS.CREATE, userData);
    return data;
  },

  /**
   * Update a user (Admin only)
   * @param {string} id - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(id, userData) {
    const { data } = await apiClient.put(
      API_ENDPOINTS.USERS.BY_ID(id),
      userData,
    );
    return data;
  },

  /**
   * Ask the owner to delete MY account. Nothing is deleted now.
   * @param {string} [reason]
   * @returns {Promise<{ status: string, request_id: string, requested_at: string, message: string }>}
   */
  async requestMyDeletion(reason) {
    const { data } = await apiClient.delete(
      API_ENDPOINTS.USERS.DELETE_ME,
      reason ? { data: { reason } } : undefined
    );
    return data;
  },

  /**
   * My pending deletion request, or null.
   */
  async getMyDeletionRequest() {
    const { data } = await apiClient.get(API_ENDPOINTS.USERS.MY_DELETION_REQUEST);
    return data;
  },

  /**
   * Delete a user, or ask the owner to.
   * Owner: soft-deletes immediately (200, { message }).
   * Admin: nothing is deleted; a request is filed for the owner
   * (202, { status: "pending_review", request_id, message }).
   * @param {string} id - User ID
   * @param {string} [reason]
   * @returns {Promise<{ pending: boolean, message: string, request_id?: string }>}
   */
  async delete(id, reason) {
    const { data, status } = await apiClient.delete(
      API_ENDPOINTS.USERS.BY_ID(id),
      reason ? { data: { reason } } : undefined
    );
    return { ...data, pending: status === 202 };
  },

  /**
   * Resend a new-user invitation (account setup) email (Admin only).
   * Issues a fresh setup link valid for the full invitation window.
   * @param {string} id - User ID
   * @returns {Promise<Object>} Confirmation message
   */
  async resendInvitation(id) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.USERS.RESEND_INVITATION(id),
    );
    return data;
  },
};

export default usersService;
